import { Bike, Mountain, MountainSnow } from 'lucide-react'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import AdventureCard from './AdventureCard'
import { supabase } from '@/lib/supabase'
import { CACHE_TIMES } from '@/lib/queryClient'
import { useTranslation } from 'react-i18next'
import { localizeRows, I18N_FIELDS } from '@/i18n/localize'
import { useEnduroPricing, enduroTourPrice } from '@/hooks/useEnduroPricing'
import { useTrekPricing } from '@/hooks/useTrekPricing'
import { resolveTrekGroupPrice } from '@/services/trekPricing'

/**
 * One card per adventure family: enduro, treks, rock climbing.
 *
 * The home page used to inline the whole catalogue (3 enduro tours + 2 treks +
 * a climbing card), which meant the same products were sold twice — here and
 * on their own page. Now the home page only advertises the family and the
 * dedicated page does the comparing and the selling.
 */

type TourRow = {
  id: string
  slug: string
  title: string
  price_from_usd: number
  images: string[] | null
  i18n?: Record<string, Record<string, unknown>> | null
}

type ActivityRow = {
  id: string
  slug: string
  title: string
  price_usd: number
  images: string[] | null
  activity_type: string
  booking_info: unknown
  i18n?: Record<string, Record<string, unknown>> | null
}

/** Cheapest price + a picture, i.e. everything a category card needs. */
type Summary = { from: number | null; image: string | null }

const EMPTY: Summary = { from: null, image: null }

function summarize(
  rows: { price: number; image: string | null }[] | undefined
): Summary {
  if (!rows || rows.length === 0) return EMPTY
  const priced = rows.filter((r) => r.price > 0)
  const cheapest = priced.length
    ? priced.reduce((a, b) => (b.price < a.price ? b : a))
    : null
  return {
    from: cheapest ? cheapest.price : null,
    image: rows.find((r) => r.image)?.image ?? null,
  }
}

export default function AdventureCategories() {
  const { t, i18n } = useTranslation('home')
  const enduroPrices = useEnduroPricing()
  const { data: comptaTrekPricing } = useTrekPricing()

  const { data: tours } = useQuery({
    queryKey: ['tours', 'categories', i18n.language],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tours')
        .select('id, slug, title, price_from_usd, images, i18n')
        .eq('is_active', true)
      if (error) throw error
      return localizeRows(
        (data ?? []) as TourRow[],
        i18n.language,
        I18N_FIELDS.tours
      )
    },
    ...CACHE_TIMES.STATIC,
  })

  const { data: activities } = useQuery({
    queryKey: ['activities', 'categories', i18n.language],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('activities')
        .select(
          'id, slug, title, price_usd, images, activity_type, booking_info, i18n'
        )
        .eq('is_active', true)
        .in('activity_type', ['trekking', 'rock_climbing'])
      if (error) throw error
      return localizeRows(
        (data ?? []) as ActivityRow[],
        i18n.language,
        I18N_FIELDS.activities
      )
    },
    ...CACHE_TIMES.STATIC,
  })

  const byType = (type: string) =>
    (activities ?? []).filter((a) => a.activity_type === type)

  const enduro = summarize(
    (tours ?? []).map((tour) => ({
      price: enduroTourPrice(tour, enduroPrices),
      image: tour.images?.[0] ?? null,
    }))
  )
  const trek = summarize(
    byType('trekking').map((a) => ({
      // Same compta-sourced group price the /trek pages show, so the "from"
      // on the home page can't drift from the page it links to.
      price: resolveTrekGroupPrice(a, comptaTrekPricing),
      image: a.images?.[0] ?? null,
    }))
  )
  const climbing = summarize(
    byType('rock_climbing').map((a) => ({
      price: a.price_usd,
      image: a.images?.[0] ?? null,
    }))
  )

  const categories = [
    {
      key: 'enduro',
      to: '/tours',
      icon: Bike,
      title: t('categories.enduro.title', 'Electric Enduro Tours'),
      body: t(
        'categories.enduro.body',
        'Guided off-road rides on silent Kayo EC2i bikes — half-day, full-day, or the 6-day Xaysomboun expedition.'
      ),
      cta: t('categories.enduro.cta', 'See the tours'),
      tint: 'from-garage-primary to-garage-accent',
      ...enduro,
    },
    {
      key: 'trek',
      to: '/trek',
      icon: Mountain,
      title: t('categories.trek.title', 'Lost Valley Treks'),
      body: t(
        'categories.trek.body',
        'Jungle, caves and hidden valleys on foot with a local Lao guide — one day, or two with a night in the valley.'
      ),
      cta: t('categories.trek.cta', 'See the treks'),
      tint: 'from-emerald-700 to-stone-600',
      ...trek,
    },
    {
      key: 'climbing',
      to: '/rock-climbing',
      icon: MountainSnow,
      title: t('categories.climbing.title', 'Rock Climbing'),
      body: t(
        'categories.climbing.body',
        'Vang Vieng limestone karst, bolted routes for every level, gear and instructor included.'
      ),
      cta: t('categories.climbing.cta', 'See the climbs'),
      tint: 'from-orange-700 to-amber-600',
      ...climbing,
    },
  ]

  return (
    <section className="py-20 bg-garage-surface relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-5xl font-black text-garage-dark mb-4 font-garage-title">
            {t('categories.heading', 'Choose Your Adventure')}
          </h2>
          <p className="text-lg text-garage-text max-w-2xl mx-auto">
            {t(
              'categories.body',
              'Three ways to see Vang Vieng, all run by our own local team. Pick one to see the options, prices and what is included.'
            )}
          </p>
        </motion.div>

        <div className="grid gap-6 md:grid-cols-3">
          {categories.map((cat, idx) => (
            <AdventureCard
              key={cat.key}
              to={cat.to}
              title={cat.title}
              body={cat.body}
              cta={cat.cta}
              priceFrom={cat.from}
              fromLabel={t('categories.from', 'From')}
              image={cat.image}
              tint={cat.tint}
              icon={cat.icon}
              index={idx}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
